import { matchPath, useParams } from "react-router-dom";

import { ErrorMessage } from "@/app/components/error-message";
import { LoadingScreen } from "@/components/loading-screen";
import { useGetUserPermissions } from "@/features/account-settings/api/get-user-permissions";
import { BasicUserInformationForm } from "@/features/account-settings/components/basic-user-information.form";
import { UserAvatarForm } from "@/features/account-settings/components/user-avatar.form";
import { UserPermissionsForm } from "@/features/account-settings/components/user-permissions.form";
import { useGetOrganisation } from "@/features/organisations/api/get-organisation";
import { OnboardingHeader } from "@/features/registration/components/onboarding-header";
import { useGetUser } from "@/features/users/api/get-user";

export function AdminUserPage() {
  const { id: orgId, userId } = useParams<{ id: string; userId: string }>();
  const {
    data: user,
    isLoading,
    isError,
    isSuccess,
    error,
  } = useGetUser(userId ?? "");

  const isSuperAdminAllUsersScreen =
    matchPath("/admin/users/:id", location.pathname) !== null;

  const {
    data: permissions,
    isLoading: isPermissionsLoading,
    isError: isPermissionsError,
    isSuccess: isPermissionsSuccess,
    error: permissionsError,
  } = useGetUserPermissions(userId ?? "", orgId ?? "", {
    enabled: !isSuperAdminAllUsersScreen,
  });

  const {
    data: organisationData,
    isLoading: isOrganisationLoading,
    isSuccess: isOrganisationSuccess,
    isError: isOrganisationError,
    error: organisationError,
  } = useGetOrganisation({ id: orgId ?? "" }, { enabled: !!orgId });

  if (isLoading || isPermissionsLoading || isOrganisationLoading)
    return (
      <div>
        <OnboardingHeader title="Edit user" className="mb-6" />
        <LoadingScreen />
      </div>
    );

  const isPermissionShouldError = isSuperAdminAllUsersScreen
    ? false
    : !isPermissionsSuccess || isPermissionsError || isOrganisationError;

  if (isError || !isSuccess || isPermissionShouldError || isOrganisationError) {
    return (
      <div>
        <OnboardingHeader title="Edit User" className="mb-6" />
        <ErrorMessage error={error || permissionsError || organisationError} />
      </div>
    );
  }

  return (
    <section className="space-y-4">
      <OnboardingHeader
        title={`${user.firstName} ${user.lastName}`}
        className="mb-6"
      />

      <BasicUserInformationForm
        initialData={{
          sub: user.id,
          given_name: user.firstName,
          family_name: user.lastName,
          email: user.email,
        }}
        isEditingOwnUser={false}
      />
      <UserAvatarForm userId={user.id} avatarURL={user.avatarURL} />
      {!isSuperAdminAllUsersScreen && isOrganisationSuccess && (
        <UserPermissionsForm
          initialData={{
            roles: permissions?.roles,
            roleGroup: permissions?.roleGroup,
          }}
          userId={user.id}
          organisationData={organisationData}
        />
      )}
    </section>
  );
}
